import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * What to call a person in a dropdown.
 *
 * Users carry display_name and contractors carry name, and neither is
 * guaranteed to be set - a user row created before its owner has ever signed
 * in has only an address. Falling through to the address beats an option with
 * no text in it, which is what this page was showing.
 */
const personName = (p) =>
  p.display_name || p.name || p.email || 'Unnamed';

const AdvancedSearch = ({ onResults }) => {
  const [filters, setFilters] = useState({
    q: '',
    status: '',
    priority: '',
    category: '',
    approver: '',
    assignee: '',
    invoice: '',
    dateFrom: '',
    dateTo: ''
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  // A ticket's worker is usually a contractor, not a member of staff, so the
  // assignee filter has to offer both or it answers "no results" for most of
  // the board.
  const [contractors, setContractors] = useState([]);
  // Who may actually be named as an approver, which is not the same as who
  // exists. Being an administrator and being an approver were separated so a
  // director could have the dashboards without joining the approval queue -
  // and this filter went on offering every member of staff, including people
  // no ticket could ever be approved by. Filtering by one of them returned
  // nothing, every time, with no way to tell that from "no matches".
  const [approvers, setApprovers] = useState([]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [statsRes, priRes, catRes, usersRes, conRes, aprRes] = await Promise.all([
          fetch(`${API_BASE}/statuses`),
          fetch(`${API_BASE}/priorities`),
          fetch(`${API_BASE}/categories`),
          fetch(`${API_BASE}/users`),
          fetch(`${API_BASE}/contractors`),
          fetch(`${API_BASE}/auth/approvers`)
        ]);

        const list = (r) => (Array.isArray(r) ? r : (r?.contractors ?? r?.users ?? []));
        if (statsRes.ok) setStatuses(list(await statsRes.json()));
        if (priRes.ok) setPriorities(list(await priRes.json()));
        if (catRes.ok) setCategories(list(await catRes.json()));
        if (usersRes.ok) setUsers(list(await usersRes.json()));
        if (conRes.ok) setContractors(list(await conRes.json()));
        // Falls back to the full staff list if the approver endpoint cannot be
        // read, because a filter offering too many people is better than one
        // offering nobody.
        if (aprRes.ok) setApprovers(list(await aprRes.json()));
      } catch (err) {
        console.error('Error loading filter options:', err);
      }
    };
    loadOptions();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Shared by the form and the initial load, so the page opens showing
  // everything rather than an empty panel that gives no sense of the data.
  const runSearch = useCallback(async (activeFilters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const response = await fetch(`${API_BASE}/tickets/search?${params}`);
      const data = await response.json();
      const rows = Array.isArray(data) ? data : (data?.tickets ?? []);
      setResults(rows);
      if (onResults) onResults(rows);
    } catch (err) {
      console.error('Search error:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [onResults]);

  // No filters on mount means no query string, which the search endpoint
  // treats as "everything".
  useEffect(() => { runSearch({}); }, [runSearch]);

  const handleSearch = (e) => {
    e.preventDefault();
    runSearch(filters);
  };

  const handleReset = () => {
    const cleared = {
      q: '', status: '', priority: '', category: '',
      approver: '', assignee: '', invoice: '', dateFrom: '', dateTo: ''
    };
    setFilters(cleared);
    // Clearing the filters goes back to every ticket, not to a blank panel.
    runSearch(cleared);
  };

  return (
    <div className="advanced-search">
      <h2>Advanced Search</h2>
      <form onSubmit={handleSearch} className="search-form">
        <div className="form-grid">
          <div className="form-group">
            {/* The detail moved into the placeholder. As a label it ran to
                two lines in a 200px column and dragged its input a line below
                every dropdown beside it. */}
            <label htmlFor="adv-q">Search</label>
            <input
              id="adv-q"
              type="text"
              placeholder="Ticket #, title or description"
              value={filters.q}
              onChange={(e) => handleFilterChange('q', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Status</label>
            <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
              <option value="">All Statuses</option>
              {statuses.map(s => (
                <option key={s.id} value={s.name}>{s.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Priority</label>
            <select value={filters.priority} onChange={(e) => handleFilterChange('priority', e.target.value)}>
              <option value="">All Priorities</option>
              {priorities.map(p => (
                <option key={p.id} value={p.name}>{p.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Category</label>
            <select value={filters.category} onChange={(e) => handleFilterChange('category', e.target.value)}>
              <option value="">All Categories</option>
              {categories.map(c => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            {/* /api/users returns display_name; this read u.name, which does
                not exist on the row - so both of these dropdowns rendered a
                list of blank options, one per person, and looked like they had
                simply never been populated. */}
            <label>Approver</label>
            <select value={filters.approver} onChange={(e) => handleFilterChange('approver', e.target.value)}>
              <option value="">All Approvers</option>
              {(approvers.length > 0 ? approvers : users).map(u => (
                <option key={u.id} value={u.id}>{personName(u)}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Assigned To</label>
            <select value={filters.assignee} onChange={(e) => handleFilterChange('assignee', e.target.value)}>
              <option value="">All Assignees</option>
              {/* Staff and contractors in one list. A ticket is worked by a
                  contractor far more often than by a member of staff, and
                  offering only staff meant the filter answered "no results"
                  for most of the board. Grouped so it is obvious which is
                  which - the two are different kinds of person here. */}
              {users.length > 0 && (
                <optgroup label="Staff">
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{personName(u)}</option>
                  ))}
                </optgroup>
              )}
              {contractors.length > 0 && (
                <optgroup label="Contractors">
                  {contractors.map(c => (
                    <option key={c.id} value={c.id}>{personName(c)}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          <div className="form-group">
            {/* One invoice usually covers several tickets, so this is how you
                get from a bill a contractor has sent to the jobs on it. A
                partial number matches, because nobody types the whole thing. */}
            <label htmlFor="adv-invoice">Invoice Number</label>
            <input
              id="adv-invoice"
              type="text"
              placeholder="e.g. INV-2026-041"
              value={filters.invoice}
              onChange={(e) => handleFilterChange('invoice', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>From Date</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>To Date</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            />
          </div>
        </div>

        <div className="button-group">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            Reset
          </button>
        </div>
      </form>

      {!loading && results.length === 0 && (
        <p className="search-empty">No tickets match those filters.</p>
      )}

      {results.length > 0 && (
        <div className="search-results">
          <h3>Results ({results.length})</h3>
          <div className="results-grid">
            {results.map(ticket => (
              <Link
                key={ticket.id}
                to={`/tickets/${ticket.id}`}
                className="result-card"
                title={`Open ${ticket.ticket_number}`}
              >
                <div className="result-header">
                  <strong>{ticket.ticket_number}</strong>
                  <span className="status-badge" style={{ backgroundColor: ticket.status_color }}>
                    {ticket.status_name}
                  </span>
                </div>
                <p className="result-title">{ticket.title}</p>
                <p className="result-meta">
                  Requester: {ticket.requester_name || '—'}
                  {' · '}Category: {ticket.category_name || ticket.category || '—'}
                  {' · '}Contractor: {ticket.contractor_name || 'Unassigned'}
                </p>
                {ticket.invoice_numbers && (
                  <p className="result-meta">Invoiced on: {ticket.invoice_numbers}</p>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedSearch;
